import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fleetService } from '../services/fleetService';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { Download, DollarSign, Car } from 'lucide-react';

const COLORS = ['#00BCD4', '#4CAF50', '#FF9800', '#F44336', '#2196F3', '#9C27B0'];

export default function Analytics() {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const { data: analyticsData, isLoading } = useQuery({
    queryKey: ['fleet-analytics', startDate, endDate],
    queryFn: () => {
      return fleetService.getAnalytics({
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      }).then((res) => res.data);
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <div className="text-text-secondary dark:text-text-secondary-dark">Loading analytics...</div>
        </div>
      </div>
    );
  }

  const revenueData = analyticsData?.revenue?.byDriver || [];
  const ridesByStatus = analyticsData?.rides?.byStatus || {};
  const vehicleTypeData = analyticsData?.revenue?.byVehicleType || [];

  return (
    <div className="space-y-4 lg:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-text dark:text-text-dark mb-2">Analytics</h1>
          <p className="text-text-secondary dark:text-text-secondary-dark">
            Fleet performance and insights
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="flex gap-2">
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="px-4 py-2 bg-surface-secondary dark:bg-surface-secondary-dark border border-border dark:border-border-dark rounded-lg text-text dark:text-text-dark text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="px-4 py-2 bg-surface-secondary dark:bg-surface-secondary-dark border border-border dark:border-border-dark rounded-lg text-text dark:text-text-dark text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <button className="hidden sm:flex items-center gap-2 px-4 py-2 bg-surface dark:bg-surface-dark border border-border dark:border-border-dark rounded-lg text-sm font-medium text-text dark:text-text-dark hover:bg-surface-secondary dark:hover:bg-surface-secondary-dark transition-colors">
            <Download className="w-4 h-4" />
            Export
          </button>
        </div>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
        <div className="bg-surface dark:bg-surface-dark rounded-2xl p-4 lg:p-6 border border-border dark:border-border-dark shadow-lg w-full overflow-hidden">
          <div className="mb-4 sm:mb-6">
            <h2 className="text-lg sm:text-xl font-bold text-text dark:text-text-dark mb-1">Revenue by Driver</h2>
            <p className="text-xs sm:text-sm text-text-secondary dark:text-text-secondary-dark">Performance breakdown</p>
          </div>
          {revenueData.length > 0 ? (
            <div className="w-full" style={{ height: '300px', minHeight: '250px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={revenueData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E0E0E0" opacity={0.3} />
                  <XAxis dataKey="name" stroke="#757575" fontSize={12} />
                  <YAxis stroke="#757575" fontSize={12} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'rgba(255, 255, 255, 0.95)', 
                      border: '1px solid #E0E0E0',
                      borderRadius: '8px',
                      fontSize: '12px'
                    }} 
                  />
                  <Legend wrapperStyle={{ fontSize: '12px' }} />
                  <Bar dataKey="revenue" fill="#00BCD4" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="w-full flex items-center justify-center" style={{ height: '300px', minHeight: '250px' }}>
              <div className="flex flex-col items-center justify-center text-center px-6 py-8">
                <div className="w-16 h-16 rounded-full bg-primary/10 dark:bg-primary-dark/10 flex items-center justify-center mb-4">
                  <DollarSign className="w-8 h-8 text-primary dark:text-primary-dark opacity-50" />
                </div>
                <h3 className="text-base sm:text-lg font-semibold text-text dark:text-text-dark mb-2">
                  No Revenue Data
                </h3>
                <p className="text-sm text-text-secondary dark:text-text-secondary-dark max-w-xs leading-relaxed">
                  Revenue by driver will appear here once rides are completed
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="bg-surface dark:bg-surface-dark rounded-2xl p-4 lg:p-6 border border-border dark:border-border-dark shadow-lg w-full overflow-hidden">
          <div className="mb-4 sm:mb-6">
            <h2 className="text-lg sm:text-xl font-bold text-text dark:text-text-dark mb-1">Revenue by Vehicle Type</h2>
            <p className="text-xs sm:text-sm text-text-secondary dark:text-text-secondary-dark">Distribution analysis</p>
          </div>
          {vehicleTypeData.length > 0 ? (
            <div className="w-full" style={{ height: '300px', minHeight: '250px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={vehicleTypeData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="revenue"
                  >
                    {vehicleTypeData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'rgba(255, 255, 255, 0.95)', 
                      border: '1px solid #E0E0E0',
                      borderRadius: '8px',
                      fontSize: '12px'
                    }} 
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="w-full flex items-center justify-center" style={{ height: '300px', minHeight: '250px' }}>
              <div className="flex flex-col items-center justify-center text-center px-6 py-8">
                <div className="w-16 h-16 rounded-full bg-primary/10 dark:bg-primary-dark/10 flex items-center justify-center mb-4">
                  <Car className="w-8 h-8 text-primary dark:text-primary-dark opacity-50" />
                </div>
                <h3 className="text-base sm:text-lg font-semibold text-text dark:text-text-dark mb-2">
                  No Revenue Data
                </h3>
                <p className="text-sm text-text-secondary dark:text-text-secondary-dark max-w-xs leading-relaxed">
                  Revenue by vehicle type will appear here once rides are completed
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Rides Overview */}
      <div className="bg-surface dark:bg-surface-dark rounded-2xl p-4 lg:p-6 border border-border dark:border-border-dark shadow-lg">
        <div className="mb-6">
          <h2 className="text-xl font-bold text-text dark:text-text-dark mb-1">Rides Overview</h2>
          <p className="text-sm text-text-secondary dark:text-text-secondary-dark">Status distribution</p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {Object.entries(ridesByStatus).map(([status, count]) => (
            <div key={status} className="bg-gradient-to-br from-surface-secondary to-surface-secondary/50 dark:from-surface-secondary-dark dark:to-surface-secondary-dark/50 rounded-xl p-4 border border-border dark:border-border-dark">
              <p className="text-xs text-text-secondary dark:text-text-secondary-dark mb-2 capitalize">
                {status.replace('_', ' ')}
              </p>
              <p className="text-2xl font-bold text-text dark:text-text-dark">{count}</p>
            </div>
          ))}
          {Object.keys(ridesByStatus).length === 0 && (
            <div className="col-span-full text-center text-text-secondary dark:text-text-secondary-dark py-8">
              No ride data available
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
